import { ReactNode, useState } from 'react';
import { Image, Text } from '@belong/ui';
import clsx from 'clsx';
import css from './step-card.module.css';

export type StepCardConstantProps = {
  src: string;
  title: ReactNode;
  subtitle?: ReactNode;
  subtitleClassName?: string;
  variant?: 'default' | 'medium' | 'small';
  hoverState?: ReactNode;
  showNumbers?: boolean;
};

type StepCardProps = StepCardConstantProps & { step: number };

export function StepCard({
  step,
  src,
  title,
  subtitle,
  hoverState,
  subtitleClassName,
  variant = 'default',
  showNumbers = true,
}: StepCardProps) {
  const [showHover, setShowHover] = useState(false);
  const [singleTouch, setSingleTouch] = useState(false);

  function getTextVariant() {
    if (variant === 'medium' || variant === 'small') {
      return 'body';
    }
    return 'h3';
  }

  return (
    <div className={clsx(css.container, 'relative flex flex-col items-center w-full text-center')}>
      {showNumbers && (
        <div className={clsx(css.stepNumberContainer, 'flex justify-center items-center mb-sm rounded-full bg-navy')}>
          <Text variant="h3" fontWeight="semibold" className="text-white">
            {step}
          </Text>
        </div>
      )}
      {src && (
        <div
          className={clsx(
            'mb-xs aspect-w-3 aspect-h-2',
            variant === 'small' ? css.imageSmall : variant === 'medium' ? css.imageMedium : css.image
          )}
        >
          <Image src={src} alt="" />
        </div>
      )}
      <div className={variant === 'small' ? 'mb-xs' : 'mb-xs md:mb-sm'}>
        <Text as="div" variant={getTextVariant()} fontWeight="semibold">
          {title}
        </Text>
      </div>
      {subtitle && (
        <div className="mb-xs md:mb-sm">
          <Text className={subtitleClassName}>{subtitle}</Text>
        </div>
      )}
      {hoverState && (
        <>
          <div className={clsx(css.hoverIndicatorContainer, 'relative border-2 border-solid border-navy rounded-full')}>
            <div className="absolute inset-0 flex justify-center items-center">
              {Array.from(Array(3), (_, index) => (
                <div key={index} className={clsx(css.hoverIndicatorDot, 'bg-navy rounded-full last:mr-0')} />
              ))}
            </div>
          </div>
          <div
            onTouchStart={() => setSingleTouch(true)}
            onTouchMove={() => setSingleTouch(false)}
            onTouchEnd={() => {
              if (singleTouch) setShowHover(!showHover);
            }}
            onMouseEnter={() => setShowHover(true)}
            onMouseLeave={() => setShowHover(false)}
            className={clsx(
              'absolute inset-0 cursor-pointer xl:cursor-default transition duration-hover opacity-0',
              showHover && 'opacity-100'
            )}
          >
            {hoverState}
          </div>
        </>
      )}
    </div>
  );
}

export type HoverStateProps = {
  title: ReactNode;
  body: string;
  action?: ReactNode;
  showNumbers?: boolean;
};

function HoverState({ title, body, action, showNumbers }: HoverStateProps) {
  return (
    <div className="flex items-center justify-center w-full h-full text-center bg-white/90">
      <div
        className={clsx(
          showNumbers ? css.hoverStateContent : css.hoverStateContentWithoutNumbers,
          'flex flex-col items-center'
        )}
      >
        <div className="mb-xs">
          <Text as="div" fontWeight="semibold">
            {title}
          </Text>
        </div>
        <Text as="div">{body}</Text>
        {action && (
          <div className="relative w-full">
            <div className="absolute top-0 inset-x-0 pt-sm">{action}</div>
          </div>
        )}
      </div>
    </div>
  );
}

StepCard.HoverState = HoverState;
